import { create } from 'zustand';
import ServiceAuthSSO from '@/services/ServiceAuthSSO';
import ServiceAuth from '@/services/ServiceAuth';
import { authStorage } from '@/lib/authStorage';

export interface AuthUser {
  id?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  emailAddress?: string;
  phone?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser;
  getUser: () => Promise<AuthUser | undefined>;
}

// Dedupe de chamadas concorrentes: PageHome/TopHeader/PagePerfil montam juntos e
// disparam getUser ao mesmo tempo. Sem isto seriam N requisições idênticas ao BFF
// (que ainda faz overlay no user-service). Uma requisição em voo é compartilhada.
let inflight: Promise<AuthUser | undefined> | null = null;

export const useAuthStore = create<AuthState>((set) => ({
  token: authStorage.getItem('access_Token'),
  user: {},

  getUser() {
    if (inflight) return inflight;
    inflight = fetchUser(set).finally(() => {
      inflight = null;
    });
    return inflight;
  },
}));

async function fetchUser(
  set: (partial: Partial<AuthState>) => void,
): Promise<AuthUser | undefined> {
  const token = authStorage.getItem('access_Token');
  if (!token) return undefined;

  try {
    let user: AuthUser;

    if (ServiceAuthSSO.isSSO()) {
      const jwtUser = ServiceAuthSSO.getUserFromToken(token);
      if (!jwtUser) throw new Error('Token inválido');
      user = jwtUser;
      try {
        const { data } = await ServiceAuth.user.getUser();
        if (data) {
          user = {
            ...user,
            id: data.id || jwtUser.id,
            firstName: data.firstName || jwtUser.firstName,
            lastName: data.lastName || jwtUser.lastName,
            username: data.username || jwtUser.username,
            emailAddress: data.email || jwtUser.emailAddress,
            phone: data.phone ?? jwtUser.cellPhone ?? undefined,
          };
        }
      } catch {
        // fallback: dados do JWT
      }
    } else {
      const { data } = await ServiceAuth.user.getUser();
      if (!data) return undefined;
      user = {
        id: data.id,
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        emailAddress: data.emailAddress || data.email,
        phone: data.phone,
      };
    }

    set({ user, token });
    return user;
  } catch (e) {
    console.error('getUser failed:', e);
    return undefined;
  }
}
